import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBell, FiUser, FiCheck } from 'react-icons/fi';
import notificationService, { Notification } from '../services/NotificationService';
import './NotificationBell.css';

const NotificationBell: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [showUnreadOnly, setShowUnreadOnly] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        // Load initial notifications
        setNotifications(notificationService.getAll());

        // Listen for notification updates
        const handleUpdate = (event: CustomEvent<{ notifications: Notification[] }>) => {
            setNotifications(event.detail.notifications);
        };

        window.addEventListener('notificationUpdate', handleUpdate as EventListener);

        // Click outside to close
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            window.removeEventListener('notificationUpdate', handleUpdate as EventListener);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const unreadCount = notifications.filter(n => !n.isRead).length;

    const displayedNotifications = showUnreadOnly
        ? notifications.filter(n => !n.isRead)
        : notifications;

    const handleMarkAsRead = (notification: Notification) => {
        notificationService.markAsRead(notification.id);
    };

    const handleNavigateToLead = (e: React.MouseEvent, notification: Notification) => {
        e.stopPropagation();

        // Mark as read
        notificationService.markAsRead(notification.id);

        // Close dropdown
        setIsOpen(false);

        // Store pending contact for ChatWeb to pick up on mount
        sessionStorage.setItem('pendingContactId', String(notification.contactId));

        // Navigate to chat
        navigate('/chat');

        // Dispatch event for when ChatWeb is already mounted
        setTimeout(() => {
            window.dispatchEvent(new CustomEvent('openContactInChat', {
                detail: { contactId: notification.contactId }
            }));
        }, 100);
    };

    const handleMarkAllRead = () => {
        notificationService.markAllAsRead();
    };

    const formatTime = (timestamp: string) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    return (
        <div className="notification-bell" ref={dropdownRef}>
            <button
                className={`notification-bell-button ${unreadCount > 0 ? 'has-unread' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                title="Notifications"
            >
                <FiBell size={20} />
                {unreadCount > 0 && (
                    <span className="notification-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="notification-dropdown">
                    <div className="notification-header">
                        <h3>Notifications</h3>
                        <div className="notification-actions">
                            <div className="notification-toggle">
                                <span>Unread</span>
                                <div
                                    className={`notification-toggle-switch ${showUnreadOnly ? 'active' : ''}`}
                                    onClick={() => setShowUnreadOnly(!showUnreadOnly)}
                                />
                            </div>
                            {unreadCount > 0 && (
                                <button className="mark-all-read" onClick={handleMarkAllRead}>
                                    <FiCheck size={12} /> Mark all read
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="notification-list">
                        {displayedNotifications.length === 0 ? (
                            <div className="notification-empty">
                                <FiBell size={32} />
                                <p>{showUnreadOnly ? 'No unread notifications' : 'No notifications'}</p>
                            </div>
                        ) : (
                            displayedNotifications.map(notification => (
                                <div
                                    key={notification.id}
                                    className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                                    onClick={() => handleMarkAsRead(notification)}
                                >
                                    <div className="notification-icon">
                                        <FiUser size={18} />
                                    </div>
                                    <div className="notification-content">
                                        <p className="notification-message">
                                            <strong>{notification.contactName}</strong> is waiting for human assistance
                                        </p>
                                        <p
                                            className="notification-cta"
                                            onClick={(e) => handleNavigateToLead(e, notification)}
                                        >
                                            Click here to attend
                                        </p>
                                        <p className="notification-time">{formatTime(notification.timestamp)}</p>
                                    </div>
                                    {!notification.isRead && <div className="notification-unread-dot" />}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
